import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, FileText, Image as ImageIcon, Upload } from 'lucide-react'
import { type DragEvent, useRef, useState } from 'react'
import { apiClient } from '../lib/api'
import { Button } from './ui/Button'
import { Empty, Skeleton } from './ui/Empty'
import { Input, Label } from './ui/Input'

type AssetItem = {
  relativePath: string
  size: number
  lastModified: string
  publicUrl: string | null
}

type AssetsListResponse = { clientSlug: string; items: AssetItem[] }

export function ProposalAssetsTab({
  proposalId,
  readOnly = false,
}: {
  proposalId: string
  readOnly?: boolean
}) {
  const qc = useQueryClient()
  const [dragging, setDragging] = useState(false)
  const [pathPrefix, setPathPrefix] = useState('')
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const assetsQ = useQuery({
    queryKey: ['proposal-assets', proposalId],
    queryFn: () => apiClient.get<AssetsListResponse>(`/proposals/${proposalId}/assets`),
  })

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      const relPath = pathPrefix.trim()
        ? `${pathPrefix.replace(/\/+$/, '')}/${file.name}`
        : file.name
      fd.append('relativePath', relPath)
      // Use raw fetch to bypass JSON Content-Type of apiClient.post
      const res = await fetch(`/api/proposals/${proposalId}/upload-asset`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      })
      if (!res.ok) {
        const text = await res.text()
        throw new Error(`Upload failed (${res.status}): ${text}`)
      }
      return (await res.json()) as { publicUrl: string; size: number }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proposal-assets', proposalId] })
      setUploadError(null)
    },
    onError: (err) => {
      setUploadError(err instanceof Error ? err.message : String(err))
    },
  })

  async function handleFiles(files: FileList | File[]) {
    setUploadError(null)
    for (const f of Array.from(files)) {
      await upload.mutateAsync(f)
    }
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragging(false)
    if (e.dataTransfer?.files?.length) {
      void handleFiles(e.dataTransfer.files)
    }
  }

  function onDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragging(true)
  }

  function onDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragging(false)
  }

  const items = assetsQ.data?.items ?? []

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-medium text-zinc-100 mb-1.5 text-sm">Asset del proposal</h3>
        <p className="text-xs text-zinc-500 leading-relaxed">
          File caricati su MinIO sotto la cartella del cliente. L&apos;URL pubblico è disponibile
          via{' '}
          <code className="text-cyan-300">/api/storage/proposals/&lt;slug&gt;/&lt;path&gt;</code>{' '}
          (richiede login).
          {!readOnly && ' Trascina file qui o clicca per scegliere.'}
          {assetsQ.data?.clientSlug && (
            <>
              {' '}
              Cliente: <span className="font-mono text-zinc-400">{assetsQ.data.clientSlug}</span>
            </>
          )}
        </p>
      </div>

      {readOnly && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
          La proposta non è in bozza: gli asset non possono essere modificati. Per aggiungere o
          sostituire file crea una nuova revisione.
        </div>
      )}

      {!readOnly && (
        <div>
          <Label htmlFor="asset-prefix">Sotto-cartella (opzionale)</Label>
          <Input
            id="asset-prefix"
            placeholder="es. mockup, allegati, fatture"
            value={pathPrefix}
            onChange={(e) => setPathPrefix(e.target.value)}
          />
          <p className="text-[11px] text-zinc-500 mt-1">
            Se valorizzato, ogni file finirà in <code>{pathPrefix || 'X'}/&lt;nomefile&gt;</code>.
            Lascia vuoto per la root del cliente.
          </p>
        </div>
      )}

      {/* Dropzone — hidden when readOnly */}
      {!readOnly && (
        <div
          // biome-ignore lint/a11y/useSemanticElements: drop-zone needs onDrop on a div
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click()
          }}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          className={`rounded-lg border-2 border-dashed transition-all cursor-pointer p-6 text-center ${
            dragging
              ? 'border-cyan-400 bg-cyan-500/10 shadow-[0_0_20px_-6px_rgba(34,211,238,0.5)_inset]'
              : 'border-zinc-700 bg-zinc-950/50 hover:border-cyan-500/50 hover:bg-zinc-900/60'
          }`}
        >
          <Upload
            className={`mx-auto size-6 mb-2 ${dragging ? 'text-cyan-300' : 'text-zinc-500'}`}
          />
          <p className="text-sm text-zinc-300">
            {dragging ? 'Rilascia per caricare' : 'Trascina file qui o clicca per scegliere'}
          </p>
          <p className="text-xs text-zinc-500 mt-1">
            PNG, JPG, SVG, PDF, ecc. — nessun limite di dimensione
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files) void handleFiles(e.target.files)
              e.target.value = '' // allow re-selecting same file
            }}
          />
        </div>
      )}

      {upload.isPending && (
        <div className="flex items-center gap-2 text-sm text-cyan-300">
          <div className="size-3 rounded-full border-2 border-cyan-400 border-t-transparent animate-spin" />
          Caricamento in corso…
        </div>
      )}
      {uploadError && (
        <div className="text-sm text-pink-300 rounded-md bg-pink-500/10 border border-pink-500/30 px-3 py-2">
          {uploadError}
        </div>
      )}

      {/* Asset list */}
      <div>
        <h4 className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2 mt-4">
          File caricati ({items.length})
        </h4>
        {assetsQ.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: skeleton
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <Empty
            icon={FileText}
            title="Nessun asset caricato"
            description="Trascina file qui sopra per caricarli su MinIO sotto la cartella di questo cliente."
          />
        ) : (
          <ul className="space-y-1.5">
            {items.map((item) => (
              <li
                key={item.relativePath}
                className="flex items-center gap-3 rounded-md border border-zinc-800 bg-zinc-950/40 px-3 py-2 hover:border-cyan-500/30 transition-colors"
              >
                <AssetIcon path={item.relativePath} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-100 truncate">{item.relativePath}</div>
                  <div className="text-[11px] text-zinc-500 font-mono">
                    {formatBytes(item.size)} ·{' '}
                    {new Date(item.lastModified).toLocaleDateString('it-IT')}
                  </div>
                </div>
                {item.publicUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      void navigator.clipboard.writeText(item.publicUrl ?? '')
                    }}
                    title="Copia URL pubblico"
                  >
                    <Copy className="size-3.5" /> URL
                  </Button>
                )}
                {item.publicUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(item.publicUrl ?? '', '_blank')}
                    title="Apri in nuova tab"
                  >
                    Apri
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function AssetIcon({ path }: { path: string }) {
  const ext = path.split('.').pop()?.toLowerCase() ?? ''
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) {
    return <ImageIcon className="size-4 text-violet-300 shrink-0" />
  }
  return <FileText className="size-4 text-zinc-400 shrink-0" />
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(2)} MB`
}
